import { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { weakMapMemoize } from 'reselect';
import ExecutionEnvironment from 'exenv';

import { cn } from 'helpers/classnames';
import { evSearchPageClickthrough, evSwatchImpressionWrapper, nameSwatchType } from 'events/search';
import { Loader } from 'components/Loader';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import InlineProductCardAd from 'components/common/InlineProductCardAd';
import { getHeartProps } from 'helpers/HeartUtils';
import { getLowStockLabelStatus } from 'helpers/cardUtils';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import InlineAdSlot, { InlineSponsoredAds } from 'components/search/InlineAdSlot';
import useRowHasBadge from 'hooks/useRowHasBadge';
import useWindowSize from 'hooks/useWindowSize';
import ProductUtils from 'helpers/ProductUtils';
import { getBadgeId } from 'helpers/BadgeUtils';
import { useSponsoredAds } from 'hooks/useSponsoredAds';
import usePrevious from 'hooks/usePrevious';

import css from 'styles/components/search/products.scss';

const MAX_ANIMATION_FRAMES = 9;

const makeMakeCardClickExpanded = weakMapMemoize(
  (onProductClicked, dispatch, product, index, productId, styleId, isLowStock, badgeId, page) => (_, currentStyleId) => {
    const position = index + 1;
    const {
      brandName,
      defaultCategory = '',
      originalPrice,
      price,
      productName,
      productType,
      relatedStyles = [],
      txAttrFacet_Gender: genders = []
    } = product;
    const selectedStyle = relatedStyles.find(style => style.styleId === styleId);
    const isOnSale = ProductUtils.isStyleOnSale(selectedStyle);
    const gender = genders.length === 1 ? genders[0] : 'Unisex';

    const productEventData = {
      brand: brandName,
      category: productType,
      gender,
      list_price: originalPrice,
      name: productName,
      on_sale: isOnSale,
      position,
      price,
      sku: productId,
      styleId,
      subCategory: defaultCategory,
      subCategory2: ''
    };

    onProductClicked(position, productId, styleId, productEventData);
    dispatch((_, getState) => {
      const state = getState();
      const eventPayload = {
        pageNumber: page + 1,
        pageResultNumber: position,
        product,
        numberOfHearts: state?.products?.heartsList?.[styleId],
        isLowStock,
        currentStyleId,
        badgeId
      };
      track(() => [evSearchPageClickthrough, eventPayload]);
    });
  }
);

const makeMakeCardClick = weakMapMemoize((onProductClicked, dispatch, page) => (product, index) => {
  const { productId, styleId, isLowStock, badges } = product;
  const badgeId = getBadgeId(badges);
  return makeMakeCardClickExpanded(onProductClicked, dispatch, product, index, productId, styleId, isLowStock, badgeId, page);
});

const MemoColorSwatchWrapper = memo(ColorSwatchWrapper);

export const Products = ({
  products,
  onProductClicked,
  onProductMediaHovered,
  filters,
  productClass,
  products: { list, allProductsCount, inlineRecos, isLoading, oosMessaging, executedSearchUrl },
  inlineBannerData,
  trackSponsoredAdImpressions,
  isRecognizedCustomer,
  heartProduct,
  unHeartProduct,
  toggleHeartingLoginModal,
  makeScrollButton,
  crossSiteRecos,
  isLoadingSymphony,
  fetchSearchInlineRecos
}) => {
  const {
    testId,
    marketplace: {
      features: { showRatings },
      search: { msaMelodyImageParams, hasSponsoredAds, sponsoredAdsSlotOneRowIndex, sponsoredAdsSlotTwoRowIndex, showHorizontalInlineSearchRecos }
    }
  } = useMartyContext();
  const [animationTimerIndex, setAnimationTimerIndex] = useState(0);
  const { formattedList } = useRowHasBadge(list);
  const { page, selected, term, originalTerm, si, sy } = filters;
  const isMarketingCurated = !sy && si;
  const { width: windowWidth } = useWindowSize();
  const dispatch = useDispatch();
  useEffect(() => {
    const { list } = products;
    // Start a global animation timer for any products that have detail zoom.  Having a top level timer keeps them in sync regardless
    // of re-renders or lazyloading, which will tick every 800ms
    let intervalId;
    const startAnimationTimer = () => {
      intervalId = setInterval(() => {
        setAnimationTimerIndex(index => {
          let newIndex = index + 1;
          // Reset the cycle
          if (newIndex > MAX_ANIMATION_FRAMES) {
            newIndex = 0;
          }
          return newIndex;
        });
      }, 800);
    };
    list.some(item => item.animationImages?.length) && startAnimationTimer(); // if we find a product that has animation images, start timer
    return () => clearInterval(intervalId);
  }, [products]);

  // Track swatch impressions
  useEffect(() => {
    if (!windowWidth) {
      return;
    }
    const swatchImpressions = [];
    const { allProductsCount, list } = products;
    // * visible swatches are dynamically shown but this hardcoding will get us close enough to accuracy given mobile / tablet / desktop viewports for search
    const isMobile = windowWidth > 0;
    const isDesktop = windowWidth > 1138;
    const visibleSwatches = isDesktop ? 5 : isMobile ? 3 : 7;

    if (allProductsCount > 0) {
      list.forEach(({ styleId, relatedStyles } = {}) => {
        if (relatedStyles) {
          const { visibleFabricSwatches, groupedSwatches } = ProductUtils.groupVisibleSwatches(relatedStyles, visibleSwatches);

          const swatches = visibleFabricSwatches.map(({ color, styleId, swatchUrl }) => ({
            styleId,
            supplementalData: {
              swatchType: nameSwatchType(color, swatchUrl)
            }
          }));

          const groupedSwatchesProductIdentifiers = groupedSwatches.map(({ color, styleId, swatchUrl }) => ({
            styleId,
            supplementalData: {
              swatchType: nameSwatchType(color, swatchUrl)
            }
          }));

          swatchImpressions.push({
            mainStyleId: styleId,
            pageType: 'SEARCH_PAGE',
            swatches,
            groupedSwatches: groupedSwatchesProductIdentifiers
          });
        }
      });
    }

    if (swatchImpressions.length > 0) {
      track(() => [evSwatchImpressionWrapper, swatchImpressions]);
    }
  }, [products.allProductsCount, products.list, windowWidth]);

  const firstTenProductIds = ProductUtils.getFirstTenProductIds(list);

  const combinedFilters = { ...selected?.singleSelects, ...selected?.multiSelects };

  const sponsoredAds = useSponsoredAds(
    {
      filters: combinedFilters,
      pageType: Object.keys(combinedFilters).length > 0 ? 'Category' : 'SearchResults',
      term,
      matchStyles: firstTenProductIds
    },
    isMarketingCurated
  );

  const sponsoredAdsResultsTopSlot = sponsoredAds?.results.top;
  const sponsoredAdsResultsBottomSlot = sponsoredAds?.results.bottom;

  const makeCardClick = makeMakeCardClick(onProductClicked, dispatch, page);
  const prevOriginalTerm = usePrevious(originalTerm);

  const shouldHaveRecos = useCallback(
    () => showHorizontalInlineSearchRecos && ExecutionEnvironment.canUseDOM && page === 0 && !isMarketingCurated,
    [page, showHorizontalInlineSearchRecos]
  );

  useEffect(() => {
    if (!originalTerm || !shouldHaveRecos() || prevOriginalTerm === originalTerm) {
      return;
    }

    fetchSearchInlineRecos(originalTerm);
  }, [originalTerm, shouldHaveRecos(), prevOriginalTerm]);

  const makeProducts = list => {
    if (allProductsCount > 0) {
      const heartsProps = getHeartProps(
        {
          trackEvent,
          isCustomer: isRecognizedCustomer,
          heartProduct,
          toggleHeartingLoginModal,
          unHeartProduct,
          isDisplayCount: true,
          hasHearting: true
        },
        {
          heartEventName: 'TE_SEARCH_PRODUCT_HEART',
          unHeartEventName: 'TE_SEARCH_PRODUCT_UNHEART'
        }
      );

      const allProductCards = list.map((v, i) => {
        const isLowStock = getLowStockLabelStatus({ ...v });
        v.isLowStock = isLowStock;
        const product = {
          ...v,
          index: i
        };
        return (
          <MemoColorSwatchWrapper
            {...product}
            heartsInfo={heartsProps}
            key={`${product.styleId}-${product.colorId}`}
            testId={testId('searchResult')}
            animationTimerIndex={animationTimerIndex}
            msaImageParams={msaMelodyImageParams}
            onClick={makeCardClick(v, i)}
            onProductMediaHovered={onProductMediaHovered}
            link={product.productSeoUrl || product.productUrl || null}
            showRatings={showRatings}
            className={css.card}
          />
        );
      });

      if (inlineBannerData?.placement) {
        const placement = parseInt(inlineBannerData.placement, 10);
        const adIndex = placement - 1;
        const canRenderAd =
          !isNaN(placement) &&
          placement > 0 &&
          allProductCards.length + 1 >= placement &&
          inlineBannerData?.heading &&
          inlineBannerData?.src &&
          inlineBannerData?.mobilesrc;

        canRenderAd && allProductCards.splice(adIndex, 0, <InlineProductCardAd key="searchInlineHero" term={term} {...inlineBannerData} />);
      }

      const productCardsCount = allProductCards.length;

      const displaySecondRowAds = productCardsCount >= 10;

      return (
        <div id="products" data-test-id={testId('productCardContainer')} className={cn(css.products, productClass)}>
          {allProductCards}
          {executedSearchUrl && (
            <>
              <InlineAdSlot
                trackSponsoredAdImpressions={trackSponsoredAdImpressions}
                makeCardClick={makeCardClick}
                msaImageParams={msaMelodyImageParams}
                heartsInfo={heartsProps}
                inlineRecos={inlineRecos}
                filters={filters}
                crossSiteRecos={crossSiteRecos}
                isLoadingSymphony={isLoadingSymphony}
                productCardsCount={productCardsCount}
                rowIndex={sponsoredAdsSlotOneRowIndex}
                sponsoredAds={sponsoredAdsResultsTopSlot}
              />
              {hasSponsoredAds && displaySecondRowAds && (
                <InlineSponsoredAds
                  trackSponsoredAdImpressions={trackSponsoredAdImpressions}
                  makeCardClick={makeCardClick}
                  msaImageParams={msaMelodyImageParams}
                  heartsInfo={heartsProps}
                  filters={filters}
                  term={term}
                  isLoadingSymphony={isLoadingSymphony}
                  productCardsCount={productCardsCount}
                  rowIndex={sponsoredAdsSlotTwoRowIndex}
                  sponsoredAds={sponsoredAdsResultsBottomSlot}
                />
              )}
            </>
          )}
        </div>
      );
    }
    return null; // no results view handled by NoSearchResults.js
  };

  // render
  return (
    <div id="searchPage" className={'searchPage'}>
      <h2>Search Results</h2>
      {oosMessaging && (
        <p className={css.oosMessaging} data-test-id={testId('oosMessage')}>
          {oosMessaging}
        </p>
      )}
      {isLoading || formattedList.length <= 0 ? <Loader /> : makeProducts(formattedList)}
      {makeScrollButton && makeScrollButton()}
    </div>
  );
};

Products.displayName = 'Products';

Products.propTypes = {
  filters: PropTypes.object.isRequired,
  layout: PropTypes.string,
  onProductClicked: PropTypes.func.isRequired,
  onProductMediaHovered: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  products: PropTypes.object.isRequired,
  showRatings: PropTypes.bool.isRequired
};

Products.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default withErrorBoundary('Products', Products);
